import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	ContextMenuLinkItem,
	IDrawerDataListColumn,
	IIndexEndpoint,
	IShowEndpoint,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useFormatDateTime,
	useHasPermission,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PermissionName } from "../../../shared/types/permission";
import { useClippings } from "../clippings/useClippings";
import { clippingsService } from "../shared/api/clippingsService";
import { getFullNameFromContactOption } from "../shared/select/ContactSelectField";
import {
	IClippingDetailResource,
	IClippingFormValues,
	IClippingIndexResource,
	ICompanyOptionResource,
	IContactOptionResource,
} from "../shared/types";
import { ClippingCreateDrawer, ClippingUpdateDrawer } from "./ClippingDrawer";

interface BaseProps {
	service?: IIndexEndpoint<IClippingIndexResource> & IShowEndpoint<IClippingDetailResource>;
	readonly?: boolean;
}

interface ContactClippingsProps extends BaseProps {
	contact?: IContactOptionResource;
	company?: undefined;
}

interface CompanyClippingsProps extends BaseProps {
	contact?: undefined;
	company?: ICompanyOptionResource;
}

type Props = ContactClippingsProps | CompanyClippingsProps;

export const ClippingList: FC<Props> = ({
	service = clippingsService,
	contact = null,
	company = null,
	readonly = false,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const formatDateTime = useFormatDateTime();
	const { createClipping, updateClipping, deleteClipping } = useClippings();

	const emptyClipping: IClippingFormValues = useMemo(
		() => ({
			title: "",
			body: "",
			link: "",
			company: company,
			contact: contact,
			event: null,
			tenant: null,
			publishedAt: "",
			reach: null,
			mediaEquivalenceValue: null,
		}),
		[company, contact],
	);

	const canCreate = useHasPermission<PermissionName>(["clipping.create"]) && !readonly;
	const canUpdate = useHasPermission<PermissionName>(["clipping.update"]) && !readonly;
	const columns: IDrawerDataListColumn<IClippingIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Title"),
				type: "value",
				valueKey: "title",
			},
			{
				heading: t("Contact"),
				type: "value",
				valueKey: "contact",
				render: (clipping) => (clipping.contact ? getFullNameFromContactOption(clipping.contact) : ""),
			},
			{
				heading: t("Company"),
				type: "value",
				valueKey: "company.name",
			},
			{
				heading: t("Event"),
				type: "value",
				valueKey: "event.name",
			},
			{
				heading: t("Provider"),
				type: "value",
				valueKey: "provider",
			},
			{
				heading: t("Published at"),
				type: "value",
				valueKey: "publishedAt",
				render: (clipping) => formatDateTime(clipping.publishedAt),
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: (clipping, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						{!readonly && (
							<ContextMenuItem
								title={t("Edit clipping")}
								label={t("Edit clipping")}
								action={openDrawer}
								icon="edit"
								shortcut="primary"
							/>
						)}
						<ContextMenuLinkItem
							to={`/clippings/${clipping.id}`}
							title={t("View clipping")}
							label={t("View clipping")}
							icon="eye"
							shortcut="primary"
						/>
						{!readonly && (
							<ContextMenuDeleteItem
								action={() => deleteClipping(clipping.id)}
								title={t("Delete clipping")}
								label={t("Delete clipping")}
								heading={t("Delete")}
								labels={{ close: t("Close"), confirm: t("Confirm") }}
							>
								{t("Are you sure you want to delete this clipping?")}
							</ContextMenuDeleteItem>
						)}
					</ContextMenu>
				),
			},
		],
		[t, formatDateTime, readonly, deleteClipping],
	);

	return (
		<PaginatedDrawerAutoDataList
			name="clippings"
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={columns}
			listFetchConfig={laravelPaginatedListFetchConfig(service.listKey, service.index)}
			translations={{ title: t("Clippings"), empty: t("No clippings available.") }}
			initialSearch={{
				search: "",
				searchFields: [
					{ name: "title", label: t("Title") },
					{ name: "companies.name", label: t("Company name") },
					{ name: "contacts.firstName", label: t("First name") },
					{ name: "contacts.lastName", label: t("Last name") },
				],
			}}
			onRowClick={(clipping) => navigate(`/clippings/${clipping.id}`)}
			renderCreateDrawer={(drawerProps) => (
				<ClippingCreateDrawer
					{...drawerProps}
					emptyClipping={emptyClipping}
					onSubmit={(formValues) => createClipping(formValues)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<ClippingUpdateDrawer
					{...drawerProps}
					service={service}
					onSubmit={(formValues) => updateClipping(drawerProps.id, formValues)}
					isContactReadOnly={!!contact}
					isCompanyReadOnly={!!company}
				/>
			)}
		/>
	);
};
